import { useEffect, useState } from "react";
import { useHeadStore } from "@/lib/seo/head-context";
import { SITE } from "@/lib/seo/site";

export type SEOProps = {
  title?: string;
  description?: string;
  /** Path beginning with "/" — will be combined with SITE.url */
  path?: string;
  /** Absolute canonical URL (overrides path-based canonical if provided) */
  canonical?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  ogType?: "website" | "article" | "profile";
  noindex?: boolean;
  jsonLd?: object | object[];
};

/**
 * SEO component.
 *
 * - In SSR (prerender): registers metadata into the head store; the prerender
 *   script reads the store after render and injects the corresponding tags
 *   into <head>.
 * - In CSR (browser/dev): returns the head tags inline. React 19 natively
 *   hoists <title>, <meta>, <link>, and <script type="application/ld+json">
 *   into <head>, so this works without any external head library.
 */
export function SEO(props: SEOProps) {
  const store = useHeadStore();
  const [hydrated, setHydrated] = useState(false);
  useEffect(() => {
    setHydrated(true);
  }, []);
  const fullTitle = SITE.titleTemplate(props.title);
  const description = props.description ?? SITE.defaultDescription;
  const canonical =
    props.canonical ?? (props.path ? `${SITE.url}${props.path}` : undefined);
  const ogTitle = props.ogTitle ?? fullTitle;
  const ogDescription = props.ogDescription ?? description;
  const ogImage = props.ogImage ?? `${SITE.url}${SITE.defaultOgImage}`;
  const ogType = props.ogType ?? "website";
  const jsonLdArr = props.jsonLd
    ? Array.isArray(props.jsonLd)
      ? props.jsonLd
      : [props.jsonLd]
    : [];

  // SSR side: collect into store. Mutating during render is OK here because
  // prerender does a single synchronous renderToString — no concurrent renders.
  if (store) {
    store.title = fullTitle;
    store.description = description;
    if (canonical) store.canonical = canonical;
    store.ogTitle = ogTitle;
    store.ogDescription = ogDescription;
    store.ogImage = ogImage;
    store.ogType = ogType;
    if (props.noindex) store.noindex = true;
    if (jsonLdArr.length) store.jsonLd.push(...jsonLdArr);
    return null;
  }

  // CSR side: emit head tags only after hydration. The prerendered HTML
  // already contains them in <head>, so rendering them during the initial
  // client render would cause a hydration mismatch (React #418).
  if (!hydrated) return null;

  return (
    <>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {canonical && <link rel="canonical" href={canonical} />}
      {props.noindex && <meta name="robots" content="noindex, nofollow" />}
      <meta property="og:title" content={ogTitle} />
      <meta property="og:description" content={ogDescription} />
      <meta property="og:type" content={ogType} />
      {canonical && <meta property="og:url" content={canonical} />}
      <meta property="og:image" content={ogImage} />
      <meta property="og:site_name" content={SITE.name} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content={SITE.twitter} />
      <meta name="twitter:title" content={ogTitle} />
      <meta name="twitter:description" content={ogDescription} />
      <meta name="twitter:image" content={ogImage} />
      {jsonLdArr.map((obj, i) => (
        <script
          key={i}
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(obj) }}
        />
      ))}
    </>
  );
}
